// 定义产品规格类型
interface ProductSpecification {
  id: string;
  name: string;
  code: string;
  series: string;
  tagline: string;
  mainImage: string;
  size: string;
  weight: string;
  area: string;
  capacity: string;
  keyFeatures: string[];
  technicalSpecs: {
    structure: {
      frameType: string;
      wallThickness: string;
      roofType: string;
      floorLoad: string;
      windResistance: string;
      seismicRating: string;
    };
    glassSystem: {
      type: string;
      thickness: string;
      uvProtection: string;
      thermalInsulation: string;
      noiseReduction: string;
      coating: string;
    };
    smartSystems: {
      [key: string]: string;
    };
  };
  materials: Array<{
    name: string;
    icon: string;
    description: string;
  }>;
  installation: {
    time: string;
    team: string;
    foundation: string;
    delivery: string;
  };
}

export const specifications: ProductSpecification[] = [
  {
    id: 'a-solo',
    name: 'A-Solo',
    code: 'A6',
    series: 'A Series',
    tagline: 'Compact design optimised for efficiency',
    mainImage: '/images/8b41f7de70a0.webp',
    size: '5.8m × 2.25m',
    weight: '5 tons',
    area: '12.60 m²',
    capacity: '1-4 People',
    keyFeatures: [
      'Compact design optimised for efficiency',
      'Integrated smart climate control system',
      'Panoramic window with UV protection',
      'Built-in workspace with ergonomic desk',
      'Energy-efficient LED lighting system',
      'Soundproof walls for privacy',
      'Modular storage solutions',
      'Quick installation in 4-6 hours'
    ],
    technicalSpecs: {
      structure: {
        frameType: 'Zinc Steel Frame',
        wallThickness: '100mm Insulated Panels',
        roofType: 'Waterproof Composite',
        floorLoad: '300 kg/m²',
        windResistance: '120 km/h',
        seismicRating: 'Grade 8'
      },
      glassSystem: {
        type: 'Double-Glazed Low-E Glass',
        thickness: '24mm Total',
        uvProtection: '99% UV Blocking',
        thermalInsulation: 'U-value 0.8 W/m²K',
        noiseReduction: '35 dB',
        coating: 'Anti-Reflective & Self-Cleaning'
      },
      smartSystems: {
        climateControl: 'AI-Powered Temperature & Humidity Management',
        lighting: 'Circadian Rhythm Adaptive LED System',
        security: 'Smart Lock with Biometric Access',
        energyMonitor: 'Real-Time Consumption Tracking',
        airQuality: 'HEPA Filtration with CO2 Monitoring',
        voiceControl: 'Integrated Voice Assistant Compatible'
      }
    },
    materials: [
      {
        name: 'Exterior Shell',
        icon: 'ri-shield-line',
        description: 'Aircraft-grade aluminium alloy with anti-corrosion coating, designed for 20+ years durability'
      },
      {
        name: 'Insulation',
        icon: 'ri-temp-cold-line',
        description: 'High-density polyurethane foam with thermal break technology for superior energy efficiency'
      },
      {
        name: 'Windows',
        icon: 'ri-window-line',
        description: 'Triple-glazed low-E glass with UV protection and noise reduction properties'
      },
      {
        name: 'Interior Finish',
        icon: 'ri-paint-brush-line',
        description: 'Eco-friendly composite panels with antibacterial coating and easy maintenance'
      }
    ],
    installation: {
      time: '4-6 hours',
      team: '2-3 technicians',
      foundation: 'Concrete pad or adjustable feet',
      delivery: 'Flatbed truck, fully assembled'
    }
  },
  {
    id: 'a-team',
    name: 'A-Team',
    code: 'A9',
    series: 'A Series',
    tagline: 'Collaborative workspace for small teams',
    mainImage: '/images/8b41f7de70a0.webp',
    size: '9.0m × 2.15m',
    weight: '3.8 tons',
    area: '19.35 m²',
    capacity: '2-4 People',
    keyFeatures: [
      'Open-plan layout for team collaboration',
      'Dual-zone climate control system',
      'Multiple panoramic windows for natural light',
      'Integrated conference area with smart display',
      'Advanced soundproofing for privacy',
      'Modular furniture configuration',
      'High-speed network infrastructure',
      'Expandable power distribution system'
    ],
    technicalSpecs: {
      structure: {
        frameType: 'Zinc Steel Frame',
        wallThickness: '120mm Insulated Panels',
        roofType: 'Solar-Ready Composite',
        floorLoad: '350 kg/m²',
        windResistance: '140 km/h',
        seismicRating: 'Grade 8'
      },
      glassSystem: {
        type: 'Triple-Glazed Smart Glass',
        thickness: '28mm Total',
        uvProtection: '99.5% UV Blocking',
        thermalInsulation: 'U-value 0.7 W/m²K',
        noiseReduction: '40 dB',
        coating: 'Electrochromic Tinting + Self-Cleaning'
      },
      smartSystems: {
        climateControl: 'Dual-Zone AI Climate Management',
        lighting: 'Scene-Based Smart Lighting Control',
        security: 'Multi-User Access Control System',
        energyMonitor: 'Solar Integration Ready with Monitoring',
        airQuality: 'Commercial-Grade Air Purification',
        conference: 'Integrated Video Conferencing System'
      }
    },
    materials: [
      {
        name: 'Exterior Shell',
        icon: 'ri-shield-line',
        description: 'Reinforced aluminium alloy with weather-resistant powder coating for enhanced durability'
      },
      {
        name: 'Insulation',
        icon: 'ri-temp-cold-line',
        description: 'Premium polyurethane foam with vapor barrier for optimal thermal performance'
      },
      {
        name: 'Windows',
        icon: 'ri-window-line',
        description: 'Triple-glazed smart glass with electrochromic tinting and acoustic dampening'
      },
      {
        name: 'Interior Finish',
        icon: 'ri-paint-brush-line',
        description: 'Commercial-grade composite panels with antimicrobial surface treatment'
      }
    ],
    installation: {
      time: '8-10 hours',
      team: '3-4 technicians',
      foundation: 'Reinforced concrete pad',
      delivery: 'Crane-assisted placement'
    }
  },
  {
    id: 'a-pro',
    name: 'A-Pro',
    code: 'A12',
    series: 'A Series',
    tagline: 'Professional workspace with premium features',
    mainImage: '/images/8b41f7de70a0.webp',
    size: '11.5m × 2.15m',
    weight: '4.8 tons',
    area: '24.73 m²',
    capacity: '6-8 People',
    keyFeatures: [
      'Spacious professional workspace layout',
      'Multi-zone climate control system',
      'Floor-to-ceiling panoramic windows',
      'Executive meeting room with smart technology',
      'Premium acoustic insulation',
      'Customisable interior configuration',
      'Enterprise-grade network infrastructure',
      'Integrated kitchenette and restroom facilities'
    ],
    technicalSpecs: {
      structure: {
        frameType: 'Zinc Steel Frame',
        wallThickness: '150mm Insulated Panels',
        roofType: 'Solar Panel Integrated',
        floorLoad: '400 kg/m²',
        windResistance: '160 km/h',
        seismicRating: 'Grade 9'
      },
      glassSystem: {
        type: 'Premium Smart Electrochromic Glass',
        thickness: '32mm Total',
        uvProtection: '99.9% UV Blocking',
        thermalInsulation: 'U-value 0.6 W/m²K',
        noiseReduction: '45 dB',
        coating: 'Integrated Blinds + Self-Cleaning'
      },
      smartSystems: {
        climateControl: 'Multi-Zone AI Climate Optimisation',
        lighting: 'Professional Lighting Control System',
        security: 'Enterprise Access Control + CCTV',
        energyMonitor: 'Solar + Battery Storage Integration',
        airQuality: 'Medical-Grade Air Purification',
        buildingManagement: 'Centralized BMS Control System'
      }
    },
    materials: [
      {
        name: 'Exterior Shell',
        icon: 'ri-shield-line',
        description: 'Premium steel-aluminium composite with architectural-grade finish and lifetime warranty'
      },
      {
        name: 'Insulation',
        icon: 'ri-temp-cold-line',
        description: 'Multi-layer insulation system with aerogel technology for maximum efficiency'
      },
      {
        name: 'Windows',
        icon: 'ri-window-line',
        description: 'Smart electrochromic glass with integrated blinds and superior acoustic performance'
      },
      {
        name: 'Interior Finish',
        icon: 'ri-paint-brush-line',
        description: 'Luxury composite panels with customisable finishes and premium surface treatments'
      }
    ],
    installation: {
      time: '12-16 hours',
      team: '4-6 technicians',
      foundation: 'Engineered concrete foundation',
      delivery: 'Heavy crane placement'
    }
  },
  {
    id: 'z-compact',
    name: 'Z-Compact',
    code: 'Z1',
    series: 'Z Series',
    tagline: 'Cozy capsule for intimate stays',
    mainImage: '/images/8b41f7de70a0.webp',
    size: '4m × 2.8m × 2.8m',
    weight: '2.2 tons',
    area: '11.2 m²',
    capacity: '1-2 People',
    keyFeatures: [
      'Space-efficient geometric design',
      'Smart storage solutions throughout',
      'Large angular windows for natural light',
      'Integrated living and sleeping area',
      'Compact kitchenette with modern appliances',
      'Energy-efficient climate system',
      'Minimalist aesthetic with premium finishes',
      'Rapid deployment capability'
    ],
    technicalSpecs: {
      structure: {
        frameType: 'Zinc Steel Frame',
        wallThickness: '110mm Insulated Panels',
        roofType: 'Angular Composite',
        floorLoad: '320 kg/m²',
        windResistance: '130 km/h',
        seismicRating: 'Grade 8'
      },
      glassSystem: {
        type: 'Angular Triple-Glazed Glass',
        thickness: '24mm Total',
        uvProtection: '99% UV Blocking',
        thermalInsulation: 'U-value 0.8 W/m²K',
        noiseReduction: '35 dB',
        coating: 'UV Protection + Thermal Efficiency'
      },
      smartSystems: {
        climateControl: 'Compact AI Climate Management',
        lighting: 'Adaptive Smart Lighting System',
        security: 'Smart Lock with Mobile Access',
        energyMonitor: 'Energy Tracking and Optimisation',
        airQuality: 'Compact Air Purification System',
        spaceOptimisation: 'Smart Furniture Control System'
      }
    },
    materials: [
      {
        name: 'Exterior Shell',
        icon: 'ri-shield-line',
        description: 'Lightweight aluminium composite with geometric pattern and weather-resistant coating'
      },
      {
        name: 'Insulation',
        icon: 'ri-temp-cold-line',
        description: 'High-performance foam insulation with thermal bridge elimination'
      },
      {
        name: 'Windows',
        icon: 'ri-window-line',
        description: 'Angular triple-glazed glass with UV protection and thermal efficiency'
      },
      {
        name: 'Interior Finish',
        icon: 'ri-paint-brush-line',
        description: 'Minimalist composite panels with easy-clean surface and modern aesthetics'
      }
    ],
    installation: {
      time: '5-7 hours',
      team: '2-3 technicians',
      foundation: 'Adjustable leveling system',
      delivery: 'Standard flatbed truck'
    }
  },
  {
    id: 'z-standard',
    name: 'Z-Standard',
    code: 'Z2',
    series: 'Z Series',
    tagline: 'Balanced comfort and space',
    mainImage: '/images/8b41f7de70a0.webp',
    size: '6m × 3.5m × 3m',
    weight: '3.5 tons',
    area: '21 m²',
    capacity: '2-4 People',
    keyFeatures: [
      'Balanced living and working zones',
      'Geometric design with optimal space utilization',
      'Multiple angular windows for panoramic views',
      'Separate sleeping and working areas',
      'Full kitchen and bathroom facilities',
      'Advanced climate control system',
      'Premium acoustic insulation',
      'Modular interior customisation'
    ],
    technicalSpecs: {
      structure: {
        frameType: 'Zinc Steel Frame',
        wallThickness: '130mm Insulated Panels',
        roofType: 'Angular Solar-Ready',
        floorLoad: '360 kg/m²',
        windResistance: '150 km/h',
        seismicRating: 'Grade 8'
      },
      glassSystem: {
        type: 'Angular Smart Glass',
        thickness: '28mm Total',
        uvProtection: '99.5% UV Blocking',
        thermalInsulation: 'U-value 0.7 W/m²K',
        noiseReduction: '40 dB',
        coating: 'Electrochromic Tinting + Noise Reduction'
      },
      smartSystems: {
        climateControl: 'Dual-Zone AI Climate System',
        lighting: 'Scene-Based Lighting Automation',
        security: 'Multi-User Access + Monitoring',
        energyMonitor: 'Solar Integration with Storage',
        airQuality: 'Advanced Air Purification System',
        homeAutomation: 'Integrated Smart Home Control'
      }
    },
    materials: [
      {
        name: 'Exterior Shell',
        icon: 'ri-shield-line',
        description: 'Reinforced aluminium with geometric pattern and premium weather-resistant finish'
      },
      {
        name: 'Insulation',
        icon: 'ri-temp-cold-line',
        description: 'Advanced multi-layer insulation with superior thermal and acoustic properties'
      },
      {
        name: 'Windows',
        icon: 'ri-window-line',
        description: 'Angular smart glass with electrochromic tinting and noise reduction'
      },
      {
        name: 'Interior Finish',
        icon: 'ri-paint-brush-line',
        description: 'Premium composite panels with customisable finishes and antimicrobial coating'
      }
    ],
    installation: {
      time: '10-12 hours',
      team: '3-4 technicians',
      foundation: 'Reinforced concrete pad',
      delivery: 'Crane-assisted placement'
    }
  },
  {
    id: 'z-premium',
    name: 'Z-Premium',
    code: 'Z3',
    series: 'Z Series',
    tagline: 'Luxury living experience',
    mainImage: '/images/8b41f7de70a0.webp',
    size: '9m × 4.5m × 3.2m',
    weight: '5.2 tons',
    area: '40.5 m²',
    capacity: '4-6 People',
    keyFeatures: [
      'Luxury geometric design with premium finishes',
      'Expansive floor-to-ceiling angular windows',
      'Separate living, working, and sleeping zones',
      'Premium kitchen with high-end appliances',
      'Spa-quality bathroom with smart fixtures',
      'Multi-zone climate and lighting control',
      'Professional-grade soundproofing',
      'Fully customisable interior design'
    ],
    technicalSpecs: {
      structure: {
        frameType: 'Zinc Steel Frame',
        wallThickness: '160mm Insulated Panels',
        roofType: 'Solar Panel Integrated',
        floorLoad: '420 kg/m²',
        windResistance: '170 km/h',
        seismicRating: 'Grade 9'
      },
      glassSystem: {
        type: 'Premium Electrochromic Smart Glass',
        thickness: '32mm Total',
        uvProtection: '99.9% UV Blocking',
        thermalInsulation: 'U-value 0.6 W/m²K',
        noiseReduction: '45 dB',
        coating: 'Integrated Blinds + Superior Acoustics'
      },
      smartSystems: {
        climateControl: 'Multi-Zone Luxury Climate System',
        lighting: 'Professional Lighting Automation',
        security: 'Premium Security + CCTV System',
        energyMonitor: 'Solar + Battery + Grid Integration',
        airQuality: 'Medical-Grade Purification System',
        smartHome: 'Luxury Home Automation Suite'
      }
    },
    materials: [
      {
        name: 'Exterior Shell',
        icon: 'ri-shield-line',
        description: 'Premium steel-aluminium hybrid with architectural-grade geometric finish'
      },
      {
        name: 'Insulation',
        icon: 'ri-temp-cold-line',
        description: 'Luxury multi-layer insulation with aerogel technology for ultimate performance'
      },
      {
        name: 'Windows',
        icon: 'ri-window-line',
        description: 'Premium electrochromic smart glass with integrated blinds and superior acoustics'
      },
      {
        name: 'Interior Finish',
        icon: 'ri-paint-brush-line',
        description: 'Luxury composite panels with bespoke finishes and premium surface treatments'
      }
    ],
    installation: {
      time: '14-18 hours',
      team: '5-6 technicians',
      foundation: 'Engineered foundation system',
      delivery: 'Heavy crane + specialist team'
    }
  }
];

export const specificationsData: Record<string, any> = {
  'a-solo': specifications[0],
  'a-team': specifications[1],
  'a-pro': specifications[2],
  'z-compact': specifications[3],
  'z-standard': specifications[4],
  'z-premium': specifications[5]
};